import {Injectable} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AppState} from "../../../store/state/app.state";
import {UmbrellaValidators} from "../../../umbrella-common/validation/umbrella-validators";
import {Country, Order} from "../../../store/state/order.state";
import {OrderActions} from "../../../store/actions/order.actions";
import {TranslateService} from "@ngx-translate/core";
import {ValidationMsg} from "../../../umbrella-common/services/standard-error-map";
import {StandaloneFormService} from "../../../umbrella-common/services/standalone-form.service";
import {BaseOrderFormService} from "../common/base-order-form.service";

@Injectable()
export class CompanyAddressBillingFormService extends StandaloneFormService implements BaseOrderFormService {

	readonly FORM_CONTROL_ERROR_MSG_MAP: { [validationKey: string]: ValidationMsg[] } = {
		requiredLength: [{msg: 'Zip code must contain 5 digits'}],
		pattern_us: [
			{formControlName: 'zipCode', msg: 'Zip code must contain 5 digits'}
		],
		pattern_ca: [
			{formControlName: 'zipCode', msg: 'E.g. M6K 1V2'}
		],
		pattern: [
			{formControlName: 'zipCode', msg: ''}
		]
	};

	private countries: Country[];
	private selectedCountry: Country;

	constructor(private store: Store<AppState>, private fb: FormBuilder, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [order, countries, selectedCountry]: [Order, Country[], Country]) {
		this.selectedCountry = selectedCountry;
		this.countries = countries;
		const companyAddress = order.billingInformations.billingCompany;
		const form = this.fb.group({
			companyName: this.fb.control(companyAddress ? companyAddress.name : '', [Validators.required, UmbrellaValidators.textLengthBetween(1, 100)], null),
			street: this.fb.control(companyAddress ? companyAddress.street : '', [Validators.required, UmbrellaValidators.textLengthBetween(1, 100)]),
			city: this.fb.control(companyAddress ? companyAddress.city : '', [Validators.required, UmbrellaValidators.textLengthBetween(1, 100)]),
			zipCode: this.fb.control(companyAddress ? companyAddress.zipCode : '', [Validators.required, control => UmbrellaValidators.zipCode(control, this.selectedCountry)]),
			usState: this.fb.control(companyAddress ? companyAddress.state : '', [Validators.required]),
			country: this.fb.control(this.getCountryCode(selectedCountry.name, countries), [Validators.required])
		});

		this.initFormControls(form);
		return form;
	}

	private getCountryCode(country: string, countries: Country[]): string {
		return countries.find(c => c.name == country).code;
	}

	getControlNamesInOrder(): string[] {
		return ['companyName', 'street', 'city', 'country', 'usState', 'zipCode'];
	}

	protected override getFormSpecificErrorMap(): { [p: string]: ValidationMsg[] } {
		return this.FORM_CONTROL_ERROR_MSG_MAP;
	}

	private initFormControls(form: FormGroup) {
		form.controls['companyName'].valueChanges.subscribe(companyName => this.store.dispatch(OrderActions.SET_BILLING_COMPANY_NAME({companyName})));
		form.controls['street'].valueChanges.subscribe(street => this.store.dispatch(OrderActions.SET_BILLING_STREET({street})));
		form.controls['city'].valueChanges.subscribe(city => this.store.dispatch(OrderActions.SET_BILLING_CITY({city})));
		form.controls['zipCode'].valueChanges.subscribe(zipCode => this.store.dispatch(OrderActions.SET_BILLING_ZIP_CODE({zipCode})));
		form.controls['usState'].valueChanges.subscribe(state => this.store.dispatch(OrderActions.SET_BILLING_STATE({state})));
		form.controls['country'].valueChanges.pipe().subscribe(countryCode => {
			if (this.selectedCountry.code != countryCode) {
				const statsFormControl = this.getFormControl(StandaloneFormService.FORM_KEY, 'usState');
				statsFormControl.setValue(null);

				[statsFormControl, this.getFormControl(StandaloneFormService.FORM_KEY, 'zipCode')].forEach(control => {
					control.setValue(null);
					control.markAsDirty();
					control.markAsTouched();
					control.updateValueAndValidity({emitEvent: false});
				});
			}

			this.selectedCountry = this.countries.find(c => c.code == countryCode);
			const countryName = this.selectedCountry.name;
			this.store.dispatch(OrderActions.SET_BILLING_COUNTRY({countryName}));
		});
	}

	getCompanyAddress() {
		return {
			name: this.getValueFor('companyName', StandaloneFormService.FORM_KEY),
			street: this.getValueFor('street', StandaloneFormService.FORM_KEY),
			city: this.getValueFor('city', StandaloneFormService.FORM_KEY),
			state: this.getValueFor('usState', StandaloneFormService.FORM_KEY),
			zipCode: this.getValueFor('zipCode', StandaloneFormService.FORM_KEY),
			country: this.countries.find(country => country.code == this.getValueFor('country', StandaloneFormService.FORM_KEY)).name,
		};
	}

	fillOrder(order: Order): Order {
		order.billingInformations.billingCompany = this.getCompanyAddress();
		return order;
	}
}
